import { ArrowDownTrayIcon, BuildingOffice2Icon } from '@heroicons/react/24/outline';
import ExcelJS from 'exceljs/dist/exceljs.min.js';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { formatDate } from '../../../../common-utils/formatters';
import type { RootState } from '../../../store/store';
import type { ExportableCompany } from '../../../utils/companyListExport';
import { COMPANY_EXPORT_COLUMNS, downloadWorkbook, formatSetupSteps, writeCompanyListSheet } from '../../../utils/companyListExport';
import type { CompanyListFilters } from '../../../utils/companyListFilters';
import { createCompanyListFilters, filterCompanies, getDjActivities } from '../../../utils/companyListFilters';
import { JWTHeader } from '../../../utils/JWTHeader';
import { ROUTES } from '../../../utils/routes';
import Badge from '../../common/Badge';
import MUITable from '../../common/MUITable';
import { showToast } from '../../common/Notification';
import Spinner from '../../common/Spinner';
import { ActivityBadges, CompanyListFiltersPanel, FederationLegend, FederationLogos } from '../components/CompanyListFilters';

interface CompanyListItem extends ExportableCompany {
  siren: string;
}

function CompaniesList() {
  const [companies, setCompanies] = useState<CompanyListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingCCI, setLoadingCCI] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const jwt = useSelector((state: RootState) => state.auth.token);
  const [filters, setFilters] = useState<CompanyListFilters>(createCompanyListFilters);

  const [exporting, setExporting] = useState(false);

  const fetchCompaniesPage = async(page = 1, limit = 1000, existingCompanies: CompanyListItem[] = []) => {
    try {
      const response = await fetch(`/api/companies?step=${page}&limit=${limit}&includeCCI=true`, JWTHeader(jwt));
      const data = await response.json();
      
      // Use the pagination data returned from the API
      const totalPages = data.pagination?.totalPages;
      setLoadingProgress(Math.floor((page / totalPages) * 100));
      
      const newCompanies = data.results.map(company => {
        const details = company.details || {};
        return {
          ...company,
          activities: getDjActivities(details),
          nbLotsHab: isNaN(Number(details.nbLotsHab)) ? 0 : Number(details.nbLotsHab),
          nbLotsTotal: isNaN(Number(details.nbLotsHabBurCom)) ? 0 : Number(details.nbLotsHabBurCom),
        };
      });
      
      const allCompanies = [...existingCompanies, ...newCompanies];
      
      // Update state with current batch of companies
      setCompanies(allCompanies);
      
      // Check if there are more pages to fetch
      if (data.pagination?.nextPage) {
        // Fetch next page using the nextPage value from pagination
        await fetchCompaniesPage(data.pagination.nextPage, limit, allCompanies);
        return;
      }
      
      // All pages fetched
      setLoading(false);
      setLoadingCCI(false);
      setLoadingProgress(100);
    } catch (error) {
      console.error('Error fetching companies page:', error);
      showToast('error', 'Une erreur est survenue lors du chargement des entreprises.');
      setLoading(false);
      setLoadingCCI(false);
    }
  };

  useEffect(() => {
    if (!jwt) {return;}
    // Start fetching from page 1 with empty initial companies array
    fetchCompaniesPage(1, 500, []);
  }, [jwt]);

  const filteredCompanies = useMemo(() => filterCompanies(companies, filters), [companies, filters]);

  const handleViewCompany = (companyId: number) => {
    window.open(ROUTES.ADMIN_COMPANY_DETAILS.replace(':companyId', companyId.toString()), '_blank');
  };

  const headers = [
    { title: 'NOM', class: 'w-1/9' },
    { title: 'SIREN', class: 'w-1/9' },
    { title: 'FÉDÉRATION', class: 'w-1/9' },
    { title: 'ACTIVITÉ', class: 'w-1/9' },
    { title: 'NB LOTS HAB', class: 'w-1/9' },
    { title: 'NB LOTS HAB / BUR / COM', class: 'w-1/9' },
    { title: 'DATE FIN CARTE', class: 'w-1/9' },
    { title: 'ÉTAPE CONFIG VALIDE', class: 'w-1/9' },
    { title: 'ACTIONS', class: 'w-1/9' },
  ];

  const formattedData = filteredCompanies.map((company) => ({
    NOM: company.name,
    SIREN: company.siren,
    FÉDÉRATION: <FederationLogos details={company.details} />,
    ACTIVITÉ: <ActivityBadges details={company.details} loading={loadingCCI} />,
    'NB LOTS HAB': company.nbLotsHab.toLocaleString('fr-FR'),
    'NB LOTS HAB / BUR / COM': company.nbLotsTotal.toLocaleString('fr-FR'),
    'DATE FIN CARTE': loadingCCI ? (
      <div className="h-4 w-24 animate-pulse rounded bg-gray-200" />
    ) : (
      company.details.dateFinCarte ? formatDate(company.details.dateFinCarte) : ''
    ),
    'ÉTAPE CONFIG VALIDE': formatSetupSteps(company.setupSteps),
    ACTIONS: (
      <button
        onClick={() => handleViewCompany(company.id)}
        className="align-center w-full rounded bg-rgpd-blue-dark px-5 py-3 text-base whitespace-nowrap text-white shadow-md md:w-auto"
      >
        Afficher la fiche entreprise
      </button>
    ),
  }));

  // Add this new function to handle the Excel export
  const exportToExcel = async() => {
    if (exporting) {return;}

    if (loading) {
      showToast('warning', 'Veuillez attendre que les données soient chargées.');
      return;
    }

    if (filteredCompanies.length === 0) {
      showToast('warning', 'Aucune donnée à exporter. Veuillez ajuster vos filtres.');
      return;
    }

    setExporting(true);

    try {
      const workbook = new ExcelJS.Workbook();
      writeCompanyListSheet(workbook.addWorksheet('Liste des entreprises'), COMPANY_EXPORT_COLUMNS, filteredCompanies);
      await downloadWorkbook(workbook, `Liste_entreprises_${new Date().toISOString()}.xlsx`);

      showToast('success', 'Export réussi.');
    } catch (error) {
      console.error('Error exporting to Excel :', error);
      showToast('error', 'Une erreur est survenue lors de l\'export.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div>
      
      <div className="rounded-lg bg-white p-4 shadow-md">
        <h1 className="mb-4 flex items-center text-2xl font-bold text-gray-800">
          <BuildingOffice2Icon className="mr-2 h-8 w-8 text-rgpd-blue-dark" />
          Liste des entreprises
        </h1>
        
        {/* Add note about nb lots data source */}
        <div className="mb-4 text-sm text-gray-600">
          <p>Note : Les données concernant le nombre de lots proviennent directement du registre national d'immatriculation et non des informations saisies par l'entreprise.</p>
        </div>

        <FederationLegend />
        
        <div className="mt-8 mb-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-800">Filtres</h2>
          <button
            onClick={exportToExcel}
            className="flex items-center rounded bg-rgpd-green px-4 py-2 text-white transition-colors hover:bg-rgpd-green-dark"
            disabled={loading || exporting}
          >
            {exporting ? (
              <>
                <Spinner size="sm" className="mr-2" />
                Exportation en cours...
              </>
            ) : (
              <>
                <ArrowDownTrayIcon className="mr-2 h-5 w-5" />
                Exporter la liste
              </>
            )}
          </button>
        </div>
        <CompanyListFiltersPanel filters={filters} onChange={setFilters} />
        {loading ? (
          <div className="flex h-64 flex-col items-center justify-center">
            <Spinner size="xl" />
            <div className="mt-4 w-64">
              <div className="h-2.5 w-full rounded-full bg-gray-200">
                <div 
                  className="h-2.5 rounded-full bg-rgpd-blue-dark" 
                  style={{ width: `${loadingProgress}%` }}
                ></div>
              </div>
              <p className="mt-2 text-center text-sm text-gray-600">
                Chargement des entreprises : {loadingProgress}%
              </p>
            </div>
          </div>
        ) : filteredCompanies.length === 0 ? (
          <div className="flex h-64 items-center justify-center">
            <Badge
              mode="warning"
              text="Aucune entreprise ne correspond aux critères sélectionnés."
              size="large"
              type="solid"
            />
          </div>
        ) : (
          <MUITable
            data={formattedData}
            headers={headers}
            tableClass="w-full"
            isSearchable
            sortableColumns={['NOM', 'SIREN', 'NB LOTS HAB', 'NB LOTS HAB / BUR / COM', 'ÉTAPE CONFIG VALIDE']}
          />
        )}
      </div>
    </div>
  );
}

export default CompaniesList;
